/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import AiManagementDatetimePicker from 'components/atoms/ai_management_datetime_picker';
import { TaskStatusChange } from 'store/modules/management_store/actions';
import { ManagementMember } from 'store/modules/management_store/types';

interface BatchUpdateTasksModalProps {
  modalOpened: boolean;
  setModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCount: number;
  inProgressCount: number;
  finishedCount: number;
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  onConfirm: (
    statusChange: TaskStatusChange,
    deadline: string | null,
    responsible: ManagementMember | null,
  ) => void;
  onSuccess?: () => void;
  successMessage?: string;
  errorMessage?: string;
  confirmBlocked?: boolean;
  activeMembers?: Array<ManagementMember>;
  membersLoading?: boolean;
  currentUserId?: number;
  children?: React.ReactNode;
}

const BatchUpdateTasksModal: React.FC<BatchUpdateTasksModalProps> = ({
  modalOpened,
  setModalOpened,
  selectedCount,
  inProgressCount,
  finishedCount,
  isLoading,
  isLoaded,
  isError,
  onConfirm,
  onSuccess,
  successMessage = 'Tarefas atualizadas com sucesso!',
  errorMessage = 'Erro ao atualizar as tarefas.',
  confirmBlocked = false,
  activeMembers = [],
  membersLoading = false,
  currentUserId,
  children,
}) => {
  const [statusChange, setStatusChange] = useState<TaskStatusChange>(null);
  const [deadline, setDeadline] = useState<string>('');
  const [responsibleId, setResponsibleId] = useState<number | null>(null);
  const responsible = activeMembers.find((member) => member.id === responsibleId) || null;
  const hasChange = statusChange !== null || !!deadline || !!responsible;
  const finishing = statusChange === 'finished';

  const [actionDispatched, setActionDispatched] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const wasLoadingRef = useRef<boolean>(false);

  const isProcessing = actionDispatched && isLoading;
  const canSubmit = hasChange && !isProcessing && !showSuccess && !confirmBlocked;

  const toResponsibleOption = (member: ManagementMember) => ({
    value: member.id,
    label: member.id === currentUserId ? `${member.nome} (você)` : member.nome,
  });

  useEffect(() => {
    if (!actionDispatched) return;
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;

    if (isLoaded) {
      setShowSuccess(true);
    } else if (isError) {
      setShowError(true);
    }
  }, [actionDispatched, isLoading, isLoaded, isError]);

  const handleConfirm = () => {
    setShowError(false);
    onConfirm(statusChange, deadline || null, responsible);
    setActionDispatched(true);
  };

  const closeModal = () => {
    if (showSuccess && onSuccess) onSuccess();
    setModalOpened(false);
  };

  return (
    <Modal show={modalOpened} size="sm">
      <div className="modal-content">
        <div className="modal-header">
          <h6 className="modal-title">
            Alterar {selectedCount} {selectedCount === 1 ? 'tarefa' : 'tarefas'}
          </h6>
          <button aria-label="Close" className="close" type="button" onClick={closeModal}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          {showError && <div className="my-2 alert alert-danger text-center">{errorMessage}</div>}
          {showSuccess && (
            <div className="my-2 alert alert-success text-center">{successMessage}</div>
          )}
          <div className="mb-3">
            <label className="font-weight-bold d-block mb-2">Alterar status</label>
            <div className="custom-control custom-radio mb-1">
              <input
                className="custom-control-input"
                type="radio"
                name="batch_status_change"
                id="batch-status-finished"
                checked={statusChange === 'finished'}
                onChange={() => {
                  setStatusChange('finished');
                  setDeadline('');
                  setResponsibleId(null);
                }}
                onClick={() => statusChange === 'finished' && setStatusChange(null)}
                disabled={inProgressCount === 0}
              />
              <label className="custom-control-label" htmlFor="batch-status-finished">
                Marcar como concluída{' '}
                <small className="text-muted">
                  ({inProgressCount} {inProgressCount === 1 ? 'pendente' : 'pendentes'})
                </small>
              </label>
            </div>
            <div className="custom-control custom-radio">
              <input
                className="custom-control-input"
                type="radio"
                name="batch_status_change"
                id="batch-status-in-progress"
                checked={statusChange === 'in_progress'}
                onChange={() => setStatusChange('in_progress')}
                onClick={() => statusChange === 'in_progress' && setStatusChange(null)}
                disabled={finishedCount === 0}
              />
              <label className="custom-control-label" htmlFor="batch-status-in-progress">
                Marcar como pendente{' '}
                <small className="text-muted">
                  ({finishedCount} {finishedCount === 1 ? 'concluída' : 'concluídas'})
                </small>
              </label>
            </div>
            {finishing && (
              <small className="form-text text-muted">
                Ao marcar como concluída, o prazo e o responsável não mudam.
              </small>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="batch-deadline" className="font-weight-bold">
              Alterar prazo para
            </label>
            <AiManagementDatetimePicker
              id="batch-deadline"
              value={deadline}
              onChange={setDeadline}
              disabled={finishing}
            />
            <small className="form-text text-muted">
              Preencha para alterar o prazo de todas as tarefas selecionadas.
            </small>
          </div>
          <div className="mb-3">
            <label htmlFor="batch-responsible" className="font-weight-bold">
              Alterar responsável para
            </label>
            <Select
              inputId="batch-responsible"
              isClearable
              isSearchable
              isLoading={membersLoading}
              isDisabled={finishing}
              options={activeMembers.map(toResponsibleOption)}
              value={responsible ? toResponsibleOption(responsible) : null}
              onChange={(option) => setResponsibleId(option ? option.value : null)}
              placeholder="Manter o responsável de cada tarefa"
              noOptionsMessage={() => 'Nenhum membro encontrado'}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000000001 }) }}
            />
          </div>
          {children}
          {!hasChange && (
            <div className="alert alert-warning mb-0" role="alert">
              Selecione uma alteração de status, preencha um novo prazo ou escolha um novo
              responsável.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-md btn-secondary"
            disabled={isProcessing}
            onClick={closeModal}
          >
            {showSuccess ? 'Fechar' : 'Cancelar'}
          </button>
          {!showSuccess && (
            <button
              type="button"
              className="btn btn-md btn-primary"
              disabled={!canSubmit}
              onClick={handleConfirm}
            >
              Aplicar
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BatchUpdateTasksModal;
